import { Route } from "react-router-dom";
import AdminLayout from "@/components/shared/admin-layout";
import { RoleRoute, PermissionRoute } from "@/components/shared/guard-route";
import { Routes, Pages } from "@/constants/enums";
import { UserResponseDtoRole } from "@/types/api";

import Admin from "@/pages";
import UsersPage from "@/pages/admin/users";
import UsersNewPage from "@/pages/admin/users-new";
import UsersEditPage from "@/pages/admin/users-edit";
import UsersPermissionsPage from "@/pages/admin/users-permissions";
import RolesPage from "@/pages/admin/roles";
import RolesNewPage from "@/pages/admin/roles-new";
import RolesEditPage from "@/pages/admin/roles-edit";
import DepartmentsPage from "@/pages/admin/departments";
import ContentPage from "@/pages/admin/content";
import NotificationsPage from "@/pages/admin/notifications";
import ReportsPage from "@/pages/admin/reports";
import AuditLogsPage from "@/pages/admin/audit-logs";
import SubmissionsPage from "@/pages/admin/submissions";
import BackupsPage from "@/pages/admin/backups";
import SettingsPage from "@/pages/admin/settings";
import DocumentsPage from "@/pages/admin/documents";
import DelegationsPage from "@/pages/admin/delegations";
import ObsReportsPage from "@/pages/admin/obs-reports";
import FaqsPage from "@/pages/admin/faqs";
import ObsCreateReportPage, {
  ObsWizardStep,
} from "@/pages/admin/obs-reports-new";
import ProfilePage from "@/pages/admin/profile";

const PLATFORM_ADMIN_ROLES = [
  UserResponseDtoRole.SUPER_ADMIN,
  UserResponseDtoRole.SUB_ADMIN,
] as const;

export const adminRoutes = (
  <Route path="/" element={<AdminLayout />}>
    <Route
      index
      element={
        <PermissionRoute permission="system:view-dashboard">
          <Admin />
        </PermissionRoute>
      }
    />
    <Route
      path={Pages.USERS}
      element={
        <PermissionRoute permission="users:read">
          <UsersPage />
        </PermissionRoute>
      }
    />
    <Route
      path={`${Pages.USERS}/new`}
      element={
        <PermissionRoute permission="users:create">
          <UsersNewPage />
        </PermissionRoute>
      }
    />
    <Route
      path={`${Pages.USERS}/:id/edit`}
      element={
        <PermissionRoute permission="users:update">
          <UsersEditPage />
        </PermissionRoute>
      }
    />
    <Route
      path={`${Pages.USERS}/permissions`}
      element={
        <PermissionRoute permission="users:manage-editor-permissions">
          <UsersPermissionsPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Pages.ROLES}
      element={
        <PermissionRoute permission="users:manage-permissions">
          <RolesPage />
        </PermissionRoute>
      }
    />
    <Route
      path={`${Pages.ROLES}/new`}
      element={
        <PermissionRoute permission="users:manage-permissions">
          <RolesNewPage />
        </PermissionRoute>
      }
    />
    <Route
      path={`${Pages.ROLES}/:code/edit`}
      element={
        <PermissionRoute permission="users:manage-permissions">
          <RolesEditPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Routes.DEPARTMENTS}
      element={
        <RoleRoute allowedRoles={[...PLATFORM_ADMIN_ROLES]}>
          <DepartmentsPage />
        </RoleRoute>
      }
    />
    <Route
      path="pages"
      element={
        <PermissionRoute permission="content:edit">
          <ContentPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Pages.NOTIFICATIONS}
      element={
        <PermissionRoute permission="system:view-dashboard">
          <NotificationsPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Pages.ANALYTICS}
      element={
        <PermissionRoute
          permission="system:view-analytics"
          deniedMessage="عذراً، ليس لديك صلاحية عرض التحليلات والتقارير."
        >
          <ReportsPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Routes.SUBMISSIONS}
      element={
        // Same gate the API applies (requirePermission 'system:view-analytics') — FR-1.9: only
        // users with access to Submissions reach the list, and so the attachment download.
        <PermissionRoute
          permission="system:view-analytics"
          deniedMessage="عذراً، ليس لديك صلاحية عرض الطلبات والاستفسارات."
        >
          <SubmissionsPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Routes.AUDIT_LOGS}
      element={
        <PermissionRoute
          permission="system:view-audit-logs"
          deniedMessage="عذراً، ليس لديك صلاحية عرض سجل التدقيق."
        >
          <AuditLogsPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Routes.BACKUPS}
      element={
        <PermissionRoute permission="system:backup-restore">
          <BackupsPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Pages.SETTINGS}
      element={
        <PermissionRoute
          permission="system:manage-settings"
          deniedMessage="عذراً، ليس لديك صلاحية إدارة إعدادات النظام."
        >
          <SettingsPage />
        </PermissionRoute>
      }
    />
    <Route path={Routes.PROFILE} element={<ProfilePage />} />
    <Route
      path={Routes.OBS_REPORTS}
      element={
        <PermissionRoute permission="obs:manage">
          <ObsReportsPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Routes.DELEGATIONS}
      element={
        <PermissionRoute permission="delegations:manage">
          <DelegationsPage />
        </PermissionRoute>
      }
    />
    <Route
      path={Routes.FAQS}
      element={
        <PermissionRoute permission="faq:manage">
          <FaqsPage />
        </PermissionRoute>
      }
    />
    {/*
      The create wizard is a LAYOUT with one child route per step, not six pages.
      The layout owns the draft, so it stays mounted while `:step` changes and
      nothing typed is lost moving between them — a flat list of six routes would
      remount the state on every «السابق».

      The index route renders too, rather than redirecting: the layout resolves
      the bare URL to wherever the operator left off and corrects the address
      itself, so the step is drawn immediately instead of flashing empty.
    */}
    <Route
      path={`${Routes.OBS_REPORTS}/new`}
      element={
        <PermissionRoute permission="obs:manage">
          <ObsCreateReportPage />
        </PermissionRoute>
      }
    >
      <Route index element={<ObsWizardStep />} />
      <Route path=":step" element={<ObsWizardStep />} />
    </Route>
    <Route
      path={Routes.DOCUMENTS}
      element={
        <PermissionRoute permission="documents:read">
          <DocumentsPage />
        </PermissionRoute>
      }
    />
  </Route>
);
